// src/components/CatalogGallery/CatalogGallerySection.tsx
"use client";

import { useState, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { motion, AnimatePresence } from "motion/react";
import { X, FileText, Lock, MessageSquare, ArrowRight } from "lucide-react";
import { catalogsData, type CatalogItem } from "@/data/catalogs";

export function CatalogGallerySection() {
  const [activePdfUrl, setActivePdfUrl] = useState<string | null>(null);
  const [openedCatalogIds, setOpenedCatalogIds] = useState<string[]>([]);
  const [showContactPopup, setShowContactPopup] = useState<boolean>(false);

  // Sync state with LocalStorage on mount so it survives page refreshes
  useEffect(() => {
    const savedHistory = localStorage.getItem("viewed_catalogs_history");
    if (savedHistory) {
      try {
        setOpenedCatalogIds(JSON.parse(savedHistory));
      } catch (e) {
        console.error("Error reading catalog logs", e);
      }
    }
  }, []);

  const handleCatalogClick = (catalog: CatalogItem) => {
    // Check if this catalog ID exists in persistent storage history
    if (openedCatalogIds.includes(catalog.id)) {
      setShowContactPopup(true);
      return;
    }

    // Launch PDF viewer and append tracking securely into localStorage
    const updatedHistory = [...openedCatalogIds, catalog.id];
    setOpenedCatalogIds(updatedHistory);
    localStorage.setItem(
      "viewed_catalogs_history",
      JSON.stringify(updatedHistory)
    );

    // Append parameters to suppress toolbar, downloading, and printing menus on compatible viewports
    setActivePdfUrl(`${catalog.pdfUrl}#toolbar=0&navpanes=0&scrollbar=0`);
  };

  const handleContactAction = () => {
    // Replace with your preferred contact flow
    window.location.href =
      "mailto:info@yourdomain.com?subject=Catalog Access Request";
    setShowContactPopup(false);
  };

  return (
    <section className="w-full bg-white px-6 py-16 md:px-12 lg:px-20 select-none">
      <div className="mx-auto max-w-[1340px]">

        {/* Editorial Section Headline */}
        <div className="mb-12 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div className="flex flex-col items-start max-w-[600px]">
            <span className="text-[12px] font-bold tracking-[0.3em] text-[#a49d91] uppercase mb-3">
              Digital Catalogs
            </span>
            <h2 className="text-[clamp(2rem,4vw,3.5rem)] font-black leading-[1.1] tracking-tight text-neutral-900 uppercase">
              Explore Our Handcrafted <br />Design Collections
            </h2>
            <p className="mt-4 text-[14.5px] leading-relaxed text-neutral-500 font-light">
              Click on any design project quadrant below to view our
              comprehensive immersive product layout booklets instantly.
            </p>
          </div>

          {/* See All Button — desktop */}
          <Link href="/catalogues" className="hidden md:block">
            <motion.button
              whileHover={{ x: 4 }}
              whileTap={{ scale: 0.97 }}
              className="flex items-center gap-3 bg-neutral-900 text-white px-7 py-4 rounded-full text-xs font-bold uppercase tracking-widest hover:bg-neutral-800 transition-colors cursor-pointer shrink-0"
            >
              See All Catalogues
              <ArrowRight className="h-4 w-4" />
            </motion.button>
          </Link>
        </div>

        {/* Gallery Grid Matching Layouts — YOUR ORIGINAL STRUCTURE UNCHANGED */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 h-auto lg:h-[650px]">

          {/* Big Quad Left Element */}
          <motion.div
            whileHover={{ y: -4 }}
            onClick={() => handleCatalogClick(catalogsData[0])}
            className="relative w-full h-[350px] md:h-full rounded-[24px] overflow-hidden bg-neutral-100 border border-neutral-100 cursor-pointer group"
          >
            <Image
              src={catalogsData[0].imageSrc}
              alt={catalogsData[0].imageAlt}
              fill
              priority
              sizes="(max-width: 768px) 100vw, 50vw"
              className="object-cover transition-transform duration-700 ease-out group-hover:scale-103"
            />
            <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
              <span className="bg-white text-neutral-900 px-6 py-3 rounded-full text-xs font-bold uppercase tracking-wider shadow-sm flex items-center gap-2">
                <FileText className="h-4 w-4" /> View Catalog
              </span>
            </div>
          </motion.div>

          {/* Combined Multirational Right Layout Frame */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-rows-[1fr_1.1fr] gap-5 h-full">

            {/* Top Row Spanned Block */}
            <motion.div
              whileHover={{ y: -4 }}
              onClick={() => handleCatalogClick(catalogsData[1])}
              className="relative w-full h-[220px] sm:h-auto sm:col-span-2 rounded-[24px] overflow-hidden bg-neutral-100 border border-neutral-100 cursor-pointer group"
            >
              <Image
                src={catalogsData[1].imageSrc}
                alt={catalogsData[1].imageAlt}
                fill
                sizes="(max-width: 768px) 100vw, 50vw"
                className="object-cover transition-transform duration-700 ease-out group-hover:scale-103"
              />
              <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                <span className="bg-white text-neutral-900 px-6 py-3 rounded-full text-xs font-bold uppercase tracking-wider shadow-sm flex items-center gap-2">
                  <FileText className="h-4 w-4" /> View Catalog
                </span>
              </div>
            </motion.div>

            {/* Bottom Row Left Quad Box */}
            <motion.div
              whileHover={{ y: -4 }}
              onClick={() => handleCatalogClick(catalogsData[2])}
              className="relative w-full h-[220px] sm:h-auto rounded-[24px] overflow-hidden bg-neutral-100 border border-neutral-100 cursor-pointer group"
            >
              <Image
                src={catalogsData[2].imageSrc}
                alt={catalogsData[2].imageAlt}
                fill
                sizes="(max-width: 640px) 100vw, 25vw"
                className="object-cover transition-transform duration-700 ease-out group-hover:scale-103"
              />
              <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                <span className="bg-white text-neutral-900 px-5 py-2.5 rounded-full text-xs font-bold uppercase tracking-wider shadow-sm flex items-center gap-2">
                  <FileText className="h-3.5 w-3.5" /> View
                </span>
              </div>
            </motion.div>

            {/* Bottom Row Right Quad Box */}
            <motion.div
              whileHover={{ y: -4 }}
              onClick={() => handleCatalogClick(catalogsData[3])}
              className="relative w-full h-[220px] sm:h-auto rounded-[24px] overflow-hidden bg-neutral-100 border border-neutral-100 cursor-pointer group"
            >
              <Image
                src={catalogsData[3].imageSrc}
                alt={catalogsData[3].imageAlt}
                fill
                sizes="(max-width: 640px) 100vw, 25vw"
                className="object-cover transition-transform duration-700 ease-out group-hover:scale-103"
              />
              <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                <span className="bg-white text-neutral-900 px-5 py-2.5 rounded-full text-xs font-bold uppercase tracking-wider shadow-sm flex items-center gap-2">
                  <FileText className="h-3.5 w-3.5" /> View
                </span>
              </div>
            </motion.div>

          </div>
        </div>

        {/* 5th Card — Full Width Strip below the main grid */}
        <motion.div
          whileHover={{ y: -4 }}
          onClick={() => handleCatalogClick(catalogsData[4])}
          className="relative mt-5 w-full h-[200px] md:h-[220px] rounded-[24px] overflow-hidden bg-neutral-100 border border-neutral-100 cursor-pointer group"
        >
          <Image
            src={catalogsData[4].imageSrc}
            alt={catalogsData[4].imageAlt}
            fill
            sizes="100vw"
            className="object-cover transition-transform duration-700 ease-out group-hover:scale-103"
          />
          <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
            <span className="bg-white text-neutral-900 px-6 py-3 rounded-full text-xs font-bold uppercase tracking-wider shadow-sm flex items-center gap-2">
              <FileText className="h-4 w-4" /> View Catalog
            </span>
          </div>
        </motion.div>

        {/* See All Button — mobile only */}
        <div className="mt-8 flex justify-center md:hidden">
          <Link href="/catalogues">
            <motion.button
              whileTap={{ scale: 0.97 }}
              className="flex items-center gap-3 bg-neutral-900 text-white px-7 py-4 rounded-full text-xs font-bold uppercase tracking-widest hover:bg-neutral-800 transition-colors cursor-pointer"
            >
              See All Catalogues <ArrowRight className="h-4 w-4" />
            </motion.button>
          </Link>
        </div>

      </div>

      {/* MODAL 1: Secure Protected Document Viewer Overlay Component */}
      <AnimatePresence>
        {activePdfUrl && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex flex-col bg-neutral-950/95 p-4 md:p-8 select-none"
            onContextMenu={(e) => e.preventDefault()}
          >
            <div className="flex justify-between items-center w-full max-w-7xl mx-auto mb-4 text-white">
              <h4 className="text-xs font-bold uppercase tracking-widest text-neutral-400">
                Secure Digital Preview (Protected View)
              </h4>
              <button
                onClick={() => setActivePdfUrl(null)}
                className="flex items-center gap-2 bg-white/10 hover:bg-white/20 px-5 py-2.5 rounded-full text-xs font-bold uppercase tracking-wider cursor-pointer transition-colors"
              >
                Close View <X className="h-4 w-4" />
              </button>
            </div>

            <div className="w-full max-w-7xl mx-auto flex-1 bg-neutral-900 rounded-xl overflow-hidden shadow-2xl relative">
              <object
                data={activePdfUrl}
                type="application/pdf"
                className="w-full h-full"
              >
                <iframe
                  src={activePdfUrl}
                  className="w-full h-full border-0"
                  title="Protected Portfolio View"
                />
              </object>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* MODAL 2: Locked Repeated Access Contact Us Alert Box */}
      <AnimatePresence>
        {showContactPopup && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-950/70 p-4 backdrop-blur-md"
          >
            <motion.div
              initial={{ scale: 0.95, y: 15 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 15 }}
              className="bg-white w-full max-w-md rounded-2xl p-8 shadow-2xl text-center flex flex-col items-center"
            >
              <div className="h-14 w-14 bg-amber-50 rounded-full flex items-center justify-center text-amber-600 mb-5">
                <Lock className="h-6 w-6" />
              </div>

              <h3 className="text-lg font-black uppercase text-neutral-900 tracking-tight">
                Access Restricted
              </h3>

              <p className="mt-3 text-[14px] leading-relaxed text-neutral-500 font-light">
                This catalog link has already been opened once. To access or
                download this document again, please contact us directly.
              </p>

              {/* Actions Grid Block */}
              <div className="mt-8 flex flex-col sm:flex-row gap-3 w-full">
                <button
                  type="button"
                  onClick={handleContactAction}
                  className="flex-1 rounded-md bg-neutral-900 py-3.5 text-xs font-bold uppercase tracking-widest text-white hover:bg-neutral-800 transition-colors cursor-pointer flex items-center justify-center gap-2"
                >
                  <MessageSquare className="h-3.5 w-3.5" /> Contact Now
                </button>

                <button
                  type="button"
                  onClick={() => setShowContactPopup(false)}
                  className="flex-1 rounded-md bg-neutral-100 py-3.5 text-xs font-bold uppercase tracking-widest text-neutral-700 hover:bg-neutral-200 transition-colors cursor-pointer"
                >
                  Close Window
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}